import { Clipboard } from '@capacitor/clipboard';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import ConditionalTooltip from '@components/ConditionalTooltip';
import FolderForm from '@components/forms/FolderForm';
import Modal from '@components/Modal';
import AlertPopover from '@components/popovers/AlertPopover';
import Popover from '@components/popovers/Popover';
import PopoverItem from '@components/popovers/PopoverItem';
import useDestroyFolder from '@hooks/mutations/useDestroyFolder';
import useOpener from '@hooks/useOpener';
import { FolderFragment, GetJourneyWithDocumentsQuery } from '@typing/Generated';

type Props = {
  folder: FolderFragment;
  journey: GetJourneyWithDocumentsQuery['journey'];
};

const FolderPopover = ({ folder, journey }: Props) => {
  const folderOpener = useOpener();
  const intl = useIntl();
  const destroyFolder = useDestroyFolder(journey);

  const opener = useOpener();

  const handleCopyInvite = useCallback(() => {
    Clipboard.write({
      string: `${import.meta.env.VITE_APP_HOST}${
        window.location.pathname.replace('/expert', '/client') + `/folders/${folder.id}`
      }`
    });

    opener.close();
  }, [folder.id, opener]);

  return (
    <div>
      <Popover color="shadesWhite" isOpen={opener.isOpen} opener={opener}>
        <AlertPopover textKey="pages.client.careProfile.careTeam.copied">
          <PopoverItem icon="copy" onClick={handleCopyInvite} textKey="pages.drive.copyLink" />
        </AlertPopover>
        <PopoverItem icon="gear" onClick={folderOpener.open} textKey="pages.drive.rename" />
        <ConditionalTooltip
          condition={journey.fileResources.some(fr => fr.folder?.id === folder.id)}
          content={intl.formatMessage({ id: 'pages.drive.hasFiles' })}
        >
          {tooltipTriggerProps => (
            <PopoverItem
              disabled={journey.fileResources.some(fr => fr.folder?.id === folder.id)}
              icon="trash"
              noBorder
              onClick={() => {
                destroyFolder({ id: folder.id });
                opener.close();
              }}
              textKey="pages.drive.delete"
              {...tooltipTriggerProps}
            />
          )}
        </ConditionalTooltip>
      </Popover>

      <Modal
        opener={folderOpener}
        title={intl.formatMessage({
          id: 'pages.documents.editFolder'
        })}
      >
        {({ modalDisplayed }) => (
          <FolderForm
            autofocus={modalDisplayed}
            folder={folder}
            journey={journey}
            onCancel={() => {
              folderOpener.close();
              opener.close();
            }}
            onSuccess={() => {
              folderOpener.close();
              opener.close();
            }}
          />
        )}
      </Modal>
    </div>
  );
};

export default FolderPopover;
